import {
  DefaultTransformer,
  DocumentObject,
  JsonObject,
  Transformer,
  deserialize,
  transform,
  whitelist,
} from '@jsonapiworld/jsonapi-serializer';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import filter from 'lodash/filter';
import isNil from 'lodash/isNil';
import {
  fetchCounterpartsMetadata,
  fetchSuggestedCounterparts,
  fetchCounterparts,
  fetchCounterpartsWithBeneficiaries,
} from '~/api/counterparts';
import { backendApiGateway } from '~/client/backend';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useFilterState } from '~/state/tableFilters';
import { AccountResourceType } from '~/types/account';
import { CategoryResourceType, SubcategoryResourceType } from '~/types/category';
import { Company, CompanyResourceType } from '~/types/company';
import { IdentityType } from '~/types/company-identity';
import {
  Counterpart,
  CounterpartCreatePayload,
  CounterpartDirection,
  CounterpartPayload,
  CounterpartKind,
  CounterpartResourceType,
  CounterpartUpdatePayload,
  CounterpartsFilters,
  CounterpartsMeta,
  CounterpartsQueryKeys,
  CounterpartWithChildren,
  CounterpartsFetchParameters,
} from '~/types/counterparts';
import { DocumentDirection, DocumentQueryKeys } from '~/types/document';
import { FlowsQueryKeys } from '~/types/flow';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { OutstandingKeys } from '~/types/outstanding';
import { isDefined } from '~/types/typeGuards';
import { defaultDestinationCode, defaultTaxNumber, defaultVatNumber } from '~/utils/counterparts';
import { downloadExcel } from '~/utils/download';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
} from '~/utils/jsonapi-query';

const PageSize = 50;

const createJsonFilters = ({
  companyId,
  direction,
  bankIdentifier,
  search,
  type,
  allowWithParent,
  ids,
  excludeIds,
  withContactEmail,
  supplierAccountId,
  clientAccountId,
}: CounterpartsFetchParameters) => {
  const filterBuilder = jsonAPIFilterBuilder();
  if (companyId) {
    filterBuilder.eq('company.id', companyId);
  }

  if (direction === CounterpartDirection.Client) {
    filterBuilder.eq('hasIssued', true);
  }
  if (direction === CounterpartDirection.Supplier) {
    filterBuilder.eq('hasReceived', true);
  }

  if (supplierAccountId) {
    filterBuilder.eq('supplierAccountId', supplierAccountId);
  }

  if (clientAccountId) {
    filterBuilder.eq('clientAccountId', clientAccountId);
  }

  if (bankIdentifier) {
    filterBuilder.eq('bankIdentifier', bankIdentifier);
  }

  if (search) filterBuilder.ilike('search', search);

  if (type) {
    filterBuilder.eq('identityType', type);
  }

  filterBuilder.neq('kind', CounterpartKind.Virtual);

  if (!allowWithParent) {
    filterBuilder.empty('parent.id', true);
  }

  if (isDefined(ids)) {
    filterBuilder.in('id', ids);
  }

  if (isDefined(excludeIds)) {
    filterBuilder.notIn('id', excludeIds);
  }

  if (withContactEmail) {
    filterBuilder.empty('contactEmail', false);
  }

  return filterBuilder;
};

class CounterpartCreateTransformer extends Transformer<CounterpartCreatePayload, {}> {
  constructor() {
    super();
    this.type = CounterpartResourceType;
    this.relationships = {
      issuedCategory: this.fieldRelatioship('issuedCategory', CategoryResourceType),
      issuedSubcategory: this.fieldRelatioship('issuedSubcategory', SubcategoryResourceType),
      receivedCategory: this.fieldRelatioship('receivedCategory', CategoryResourceType),
      receivedSubcategory: this.fieldRelatioship('receivedSubcategory', SubcategoryResourceType),
      account: this.fieldRelatioship('account', AccountResourceType),
      company: this.fieldRelatioship('company', CompanyResourceType),
    };
  }

  transform(payload: CounterpartCreatePayload): JsonObject {
    const relationships = [
      'issuedCategory',
      'issuedSubcategory',
      'receivedCategory',
      'receivedSubcategory',
      'account',
      'company',
    ];
    const keys = filter(Object.keys(payload), (val) => !relationships.includes(val));
    payload.destinationCode = defaultDestinationCode(payload.destinationCode, payload.country, payload.identityType);
    payload.taxNumber = defaultTaxNumber(payload.taxNumber, payload.country);
    payload.vatNumber = defaultVatNumber(payload.vatNumber, payload.country, payload.identityType);
    return whitelist(payload, keys);
  }
  fieldRelatioship(field: keyof CounterpartCreatePayload, resourceType: string) {
    return function (payload: CounterpartCreatePayload) {
      return transform()
        .withInput(payload[field] ? { id: payload[field] } : null)
        .withTransformer(new DefaultTransformer(resourceType))
        .toContext();
    };
  }
}

class CounterpartUpdateTransformer extends Transformer<Partial<CounterpartUpdatePayload>, {}> {
  counterpart?: Counterpart;

  constructor(counterpart?: Counterpart) {
    super();
    this.counterpart = counterpart;
    this.type = CounterpartResourceType;
    this.relationships = {
      issuedCategory: this.fieldRelatioship('issuedCategory', CategoryResourceType),
      issuedSubcategory: this.fieldRelatioship('issuedSubcategory', SubcategoryResourceType),
      receivedCategory: this.fieldRelatioship('receivedCategory', CategoryResourceType),
      receivedSubcategory: this.fieldRelatioship('receivedSubcategory', SubcategoryResourceType),
      account: this.fieldRelatioship('account', AccountResourceType),
      parent: this.fieldRelatioship('parent', CounterpartResourceType),
    };
  }

  transform(payload: Partial<CounterpartUpdatePayload>) {
    const relationships = [
      'issuedCategory',
      'issuedSubcategory',
      'receivedCategory',
      'receivedSubcategory',
      'account',
      'parent',
      'children',
    ];
    const keys = filter(Object.keys(payload), (val) => !relationships.includes(val));
    if (isDefined(this.counterpart)) {
      payload.destinationCode = defaultDestinationCode(
        payload.destinationCode ?? this.counterpart.destinationCode,
        payload.country ?? this.counterpart.country,
        payload.identityType ?? this.counterpart.identityType ?? IdentityType.Company,
      );
      payload.taxNumber = defaultTaxNumber(
        payload.taxNumber ?? this.counterpart.taxNumber ?? '',
        payload.country ?? this.counterpart.country,
      );
      payload.vatNumber = defaultVatNumber(
        payload.vatNumber ?? this.counterpart.vatNumber ?? '',
        payload.country ?? this.counterpart.country,
        payload.identityType ?? this.counterpart.identityType ?? IdentityType.Company,
      );
    }
    const transformed: Partial<CounterpartUpdatePayload> = whitelist(payload, keys);
    return transformed;
  }
  fieldRelatioship(field: keyof CounterpartUpdatePayload, resourceType: string) {
    return function (payload: Partial<CounterpartUpdatePayload>) {
      return transform()
        .withInput(isDefined(payload[field]) ? { id: payload[field] } : payload[field])
        .withTransformer(new DefaultTransformer(resourceType))
        .toContext();
    };
  }
}

const fetchCounterpart = async (id: string) => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'children',
    'issuedCategory',
    'issuedSubcategory',
    'receivedCategory',
    'receivedSubcategory',
  ]);

  const queryParams = buildJsonAPIQueryParams(includeBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: CounterpartsMeta }>(
    `/api/v1/counterparts/${id}`,
    {
      params: queryParams,
    },
  );
  return deserialize<CounterpartWithChildren>(data) as CounterpartWithChildren;
};

export const useAllCounterparts = (direction?: CounterpartDirection, search?: string) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: CounterpartsQueryKeys.all,
    queryFn: () => fetchCounterparts({ companyId, direction, search, type: null }),
  });
};

export const fetchVirtualCounterparts = async ({ companyId }: { companyId: Company['id'] }) => {
  const filterBuilder = jsonAPIFilterBuilder();
  if (companyId) {
    filterBuilder.eq('company.id', companyId);
  }

  filterBuilder.eq('kind', CounterpartKind.Virtual);

  const includeBuilder = jsonAPIIncludeBuilder(['receivedCategory', 'receivedSubcategory']);

  let cursor: MetaDataCursorPage['cursor'] = '';
  let allCounterparts: Counterpart[] = [];
  let fetchedCounterparts: Counterpart[] = [];

  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder();
    pageBuilder.size(PageSize);
    if (cursor) pageBuilder.cursor(cursor);

    const queryParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, pageBuilder);
    const { data } = await backendApiGateway.get<DocumentObject & { meta: CounterpartsMeta }>('/api/v1/counterparts', {
      params: queryParams,
    });
    fetchedCounterparts = deserialize<Counterpart>(data) as Counterpart[];
    allCounterparts = allCounterparts.concat(fetchedCounterparts);
    const { page } = pagedMetaSchema.parse(data.meta);
    cursor = page.cursor;
  } while (fetchedCounterparts.length === PageSize);

  return { counterparts: allCounterparts };
};

export const useAllVirtualCounterparts = () => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: CounterpartsQueryKeys.allVirtual(companyId),
    queryFn: () => fetchVirtualCounterparts({ companyId }),
  });
};

export const useCounterpart = (id?: string | null) => {
  return useQuery({
    queryKey: CounterpartsQueryKeys.single(id),
    queryFn: () => {
      if (!id) return null;
      return fetchCounterpart(id);
    },
  });
};

export const useCounterpartHasDocuments = (id: string) => {
  const companyId = useActiveCompanyId();
  const filters = { companyId, hasDocuments: true, ids: [id] };
  return useQuery({
    queryKey: CounterpartsQueryKeys.companyInfinityList(companyId, filters),
    queryFn: async () => {
      const data = await fetchCounterparts(filters);
      return data.counterparts.length > 0;
    },
  });
};

export const useCounterpartHasAccountId = () => {
  return useMutation({
    mutationFn: async (data: { id: Counterpart['id'] }) => {
      const counterpart = await fetchCounterpart(data.id);
      return isDefined(counterpart.account?.id);
    },
  });
};

export const useCounterpartHasCategoryId = () => {
  return useMutation({
    mutationFn: async (data: { id: Counterpart['id']; direction: DocumentDirection }) => {
      const field = data.direction === DocumentDirection.issued ? 'issuedCategory' : 'receivedCategory';
      const counterpart = await fetchCounterpart(data.id);
      return isDefined(counterpart[field]?.id);
    },
  });
};

const useCounterpartsWithBeneficiaries = (
  filters: Partial<CounterpartsFetchParameters>,
  options?: { staleTime?: number; enabled?: boolean },
) => {
  const companyId = useActiveCompanyId();
  const fullFilters: CounterpartsFetchParameters = { companyId, type: null, ...filters };

  return useInfiniteQuery({
    queryKey: CounterpartsQueryKeys.companyInfinityList(companyId, fullFilters),
    queryFn: ({ pageParam }) => fetchCounterpartsWithBeneficiaries({ pageParam, ...fullFilters }),
    initialPageParam: '',
    getNextPageParam: ({
      page: { cursor },
      counterparts,
    }: {
      page: MetaDataCursorPage;
      counterparts: Counterpart[];
    }) => {
      if (counterparts.length >= PageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId && (isNil(options?.enabled) || options.enabled),
    staleTime: isDefined(options?.staleTime) ? options.staleTime : undefined,
    placeholderData: keepPreviousData,
  });
};

export const useCounterparts = (
  filters: Partial<CounterpartsFetchParameters>,
  options?: { staleTime?: number; enabled?: boolean },
) => {
  const companyId = useActiveCompanyId();
  const fullFilters: CounterpartsFetchParameters = { companyId, type: null, ...filters };

  return useInfiniteQuery({
    queryKey: CounterpartsQueryKeys.companyInfinityList(companyId, fullFilters),
    queryFn: ({ pageParam }) => fetchCounterparts({ pageParam, ...fullFilters }),
    initialPageParam: '',
    getNextPageParam: ({
      page: { cursor },
      counterparts,
    }: {
      page: MetaDataCursorPage;
      counterparts: Counterpart[];
    }) => {
      if (counterparts.length >= PageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId && (isNil(options?.enabled) || options.enabled),
    staleTime: isDefined(options?.staleTime) ? options.staleTime : undefined,
  });
};

export const useCounterpartsTableInfiniteQuery = () => {
  const filters = useFilterState<CounterpartsFilters>();
  return useCounterparts(filters);
};

export const useCounterpartsSearch = (searchQuery: string, withContactEmail?: boolean) => {
  return useCounterparts(
    {
      search: searchQuery,
      withContactEmail,
    },
    { staleTime: Infinity },
  );
};

export const useCounterpartsWithBeneficiariesSearch = (searchQuery: string, withContactEmail?: boolean) => {
  return useCounterpartsWithBeneficiaries(
    {
      search: searchQuery,
      withContactEmail,
    },
    { staleTime: Infinity },
  );
};

const useCounterpartsMetadata = (filters: Partial<CounterpartsFetchParameters>) => {
  const companyId = useActiveCompanyId();
  const fullFilters: CounterpartsFetchParameters = { companyId, type: null, ...filters };
  return useQuery({
    queryKey: CounterpartsQueryKeys.metadata(fullFilters),
    queryFn: () => fetchCounterpartsMetadata({ filters: fullFilters }),
  });
};

const createCounterpart = async (counterpart: CounterpartPayload, companyId: Company['id']) => {
  const payload = transform<CounterpartCreatePayload, JsonObject>()
    .withInput({ ...counterpart, company: companyId })
    .withTransformer(new CounterpartCreateTransformer())
    .serialize();

  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'issuedCategory',
    'issuedSubcategory',
    'receivedCategory',
    'receivedSubcategory',
  ]);
  const queryParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/counterparts', payload, {
    params: queryParams,
  });
  return deserialize<Counterpart>(data) as Counterpart;
};

export const useCreateCounterpart = () => {
  const companyId = useActiveCompanyId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (v: CounterpartPayload) => createCounterpart(v, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CounterpartsQueryKeys.all });
    },
  });
};

const updateCounterpart = async (counterpart: Counterpart, updates: Partial<CounterpartUpdatePayload>) => {
  const payload = transform<Partial<CounterpartUpdatePayload>, {}>()
    .withInput(updates)
    .withTransformer(new CounterpartUpdateTransformer(counterpart))
    .serialize();

  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'issuedCategory',
    'issuedSubcategory',
    'receivedCategory',
    'receivedSubcategory',
    'children',
  ]);
  const queryParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/counterparts/${counterpart.id}`, payload, {
    params: queryParams,
  });
  return deserialize<CounterpartWithChildren>(data) as CounterpartWithChildren;
};

export const useUpdateCounterpart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ counterpart, data }: { counterpart: Counterpart; data: Partial<CounterpartUpdatePayload> }) =>
      updateCounterpart(counterpart, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CounterpartsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: OutstandingKeys.all });
      // Reconciliation links (transaction <-> flow) are unaffected by a
      // counterpart attribute change, so we skip invalidating their cache.
    },
  });
};

export const useBulkUpdateCounterpartsParent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ counterpartsIds, parentId }: { counterpartsIds: string[]; parentId: string }) => {
      const payload = transform<Partial<CounterpartUpdatePayload>, {}>()
        .withInput({
          parent: parentId,
        })
        .withTransformer(new CounterpartUpdateTransformer())
        .serialize();

      await Promise.all(
        counterpartsIds.map((counterpartId) =>
          backendApiGateway.patch<DocumentObject>(`/api/v1/counterparts/${counterpartId}`, payload),
        ),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CounterpartsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: OutstandingKeys.all });
      // Reconciliation links (transaction <-> flow) are unaffected by a
      // counterpart parent change, so we skip invalidating their cache.
    },
  });
};

export const useSwitchParentCounterpart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ oldParent, newParent }: { oldParent: Counterpart; newParent: Counterpart }) => {
      await updateCounterpart(newParent, { parent: null });
      await updateCounterpart(oldParent, { parent: newParent.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CounterpartsQueryKeys.all });
    },
  });
};

const exportCounterparts = async (filters: CounterpartsFetchParameters) => {
  const filterBuilder = createJsonFilters(filters);
  const sortBuilder = jsonAPISortBuilder([
    { sort: 'company_name', direction: 'asc' },
    { sort: 'id', direction: 'asc' },
  ]);
  const queryParams = buildJsonAPIQueryParams(filterBuilder, sortBuilder);

  await downloadExcel('/api/v1/counterparts', queryParams, 'controparti.xlsx');
};

export const useExportCounterparts = () => {
  const filters = useFilterState<CounterpartsFilters>();
  const companyId = useActiveCompanyId();
  const fullFilters: CounterpartsFetchParameters = { companyId, ...filters };

  return useMutation({
    mutationFn: async () => await exportCounterparts(fullFilters),
  });
};

export const useActiveCompanyCounterparts = (enabled: boolean) => {
  const company = useActiveCompany();

  return useCounterparts(
    {
      search: company.companyIdentity.taxNumber,
      type: IdentityType.Company,
    },
    {
      enabled: enabled && !!company.companyIdentity.taxNumber,
      staleTime: Infinity,
    },
  );
};

export const useCounterpartsSearchWithContactEmailMetadata = () => {
  return useCounterpartsMetadata({
    withContactEmail: true,
  });
};

export const useSuggestedCounterparts = (
  filters: Partial<CounterpartsFetchParameters>,
  options?: { staleTime?: number; enabled?: boolean },
) => {
  const companyId = useActiveCompanyId();
  const fullFilters: CounterpartsFetchParameters = { companyId, type: null, ...filters };

  return useQuery({
    queryKey: CounterpartsQueryKeys.suggested(companyId, fullFilters),
    queryFn: () => fetchSuggestedCounterparts(fullFilters),
    enabled: !!companyId && (isNil(options?.enabled) || options.enabled),
    staleTime: isDefined(options?.staleTime) ? options.staleTime : undefined,
  });
};
